import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Heart,
  Share2,
  Flag,
  MapPin,
  Bed,
  Bath,
  Square,
  Home,
  Calendar,
  CheckCircle2,
  X,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  User,
  Wifi,
  Car,
  Dumbbell,
  Shield,
  Wind,
  Flame,
  Waves,
  Building2,
  Users,
  Clock,
  DollarSign,
  CalendarCheck,
  MessageSquare,
  Edit,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { formatOriginalPrice, formatPriceWithConversion, BILLING_PERIODS } from '../utils/currency';
import { toast } from 'sonner';
import { DEMO_PROPERTIES } from '../lib/demoData';
import { motion, AnimatePresence } from 'framer-motion';
import ReviewSection from '../components/ReviewSection';
import BookingModal from '../components/BookingModal';
import SEOHead, { listingJsonLd, breadcrumbJsonLd } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const hasValidApiKey = GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY !== 'YOUR_GOOGLE_MAPS_API_KEY';

const libraries = ['places'];

const propertyTypeLabels = {
  room: 'Room',
  flat: 'Flat',
  apartment: 'Apartment',
  house: 'House',
  villa: 'Villa',
  studio: 'Studio',
  shared_room: 'Shared Room',
  shop: 'Shop',
  office: 'Office',
  commercial: 'Commercial',
};

const furnishingLabels = {
  furnished: 'Fully Furnished',
  semi_furnished: 'Semi Furnished',
  unfurnished: 'Unfurnished',
};

const facilityIcons = {
  wifi: Wifi,
  car_parking: Car,
  bike_parking: Car,
  air_conditioning: Wind,
  heating: Flame,
  water: Waves,
  cctv: Shield,
  security_guard: Shield,
  elevator: Building2,
};

const reportReasons = [
  'Fake photos',
  'Wrong location',
  'Wrong price',
  'Duplicate listing',
  'Scam behavior',
  'Misleading details',
  'Property unavailable',
  'Abusive owner',
  'Other',
];

const defaultImages = [
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&w=1200&q=80',
];

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isOwner } = useAuth();

  // Resolve demo IDs synchronously so the skeleton never flashes
  // for seeded listings — this also makes the page usable offline.
  const initialDemo =
    typeof id === 'string' && id.startsWith('demo-')
      ? DEMO_PROPERTIES.find(p => p.property_id === id) || null
      : null;

  const [property, setProperty] = useState(initialDemo);
  const [loading, setLoading] = useState(!initialDemo);
  const [isFavorite, setIsFavorite] = useState(
    Boolean(initialDemo && initialDemo.is_favorite)
  );
  // True when we're showing a demo/sample listing because the backend couldn't
  // be reached. Drives a warning banner so users don't mistake this for live data.
  const [usingDemoFallback, setUsingDemoFallback] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportLoading, setReportLoading] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: hasValidApiKey ? GOOGLE_MAPS_API_KEY : '',
    libraries,
  });

  useEffect(() => {
    let cancelled = false;

    // Fast path 1 — demo IDs and demo-session tokens resolve instantly
    // without touching the network (works offline / on slow 2G).
    const demoMatch = DEMO_PROPERTIES.find(p => p.property_id === id);
    const isDemoId = typeof id === 'string' && id.startsWith('demo-');
    const isDemoSession = localStorage.getItem('token') === 'demo-session-token';

    if (demoMatch && (isDemoId || isDemoSession)) {
      setProperty(demoMatch);
      setIsFavorite(Boolean(demoMatch.is_favorite));
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const fetchProperty = async () => {
      // Abort the request after 10 seconds so a flaky network never
      // leaves the user staring at skeletons forever.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const headers = { 'Content-Type': 'application/json' };
        const token = localStorage.getItem('token');
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(`${API_URL}/api/properties/${id}`, {
          headers,
          signal: controller.signal,
        });

        if (cancelled) return;

        if (response.ok) {
          const data = await response.json();
          if (cancelled) return;
          setProperty(data);
          setIsFavorite(Boolean(data.is_favorite));
          setUsingDemoFallback(false);
          return;
        }

        // Non-2xx — fall back to demo data if we have it.
        if (demoMatch) {
          setProperty(demoMatch);
          setIsFavorite(false);
          setUsingDemoFallback(true);
          return;
        }
        toast.error('Property not found');
        navigate('/search');
      } catch (error) {
        if (cancelled) return;
        // Network error / timeout → show demo property if we can.
        if (demoMatch) {
          setProperty(demoMatch);
          setIsFavorite(false);
          setUsingDemoFallback(true);
          return;
        }
        console.error('Property fetch failed:', error);
        toast.error('Could not load this property. Check your connection and try again.');
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) setLoading(false);
      }
    };

    fetchProperty();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save favorites');
      return;
    }

    try {
      const method = isFavorite ? 'DELETE' : 'POST';
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/favorites/${id}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setIsFavorite(!isFavorite);
        toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard');
  };

  const handleReport = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to report listings');
      return;
    }

    if (!reportReason) {
      toast.error('Please select a reason');
      return;
    }

    setReportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          property_id: id,
          reason: reportReason,
          details: reportDetails,
        }),
      });

      if (response.ok) {
        toast.success('Report submitted. We will review it shortly.');
        setReportDialogOpen(false);
        setReportReason('');
        setReportDetails('');
      }
    } catch (error) {
      toast.error('Failed to submit report');
    } finally {
      setReportLoading(false);
    }
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Get currency context for conversions - call hook unconditionally
  const currencyContext = useCurrency();
  const viewerCurrency = currencyContext?.viewerCurrency || 'USD';
  
  // Price formatting with original currency as source of truth
  const originalCurrency = property?.currency || 'USD';
  const billingPeriod = property?.billing_period || 'monthly';
  const priceInfo = property ? formatPriceWithConversion(
    property.monthly_rent,
    originalCurrency,
    viewerCurrency,
    billingPeriod
  ) : { original: '', converted: null, showConversion: false };

  const images = property?.media?.length > 0
    ? property.media.map(m => m.url)
    : defaultImages;

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="rh-container pt-20 py-12">
          <Skeleton className="h-[500px] w-full rounded-3xl mb-8" />
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-40 w-full" />
            </div>
            <div>
              <Skeleton className="h-80 w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return null;
  }

  const activeFacilities = Object.entries(property.facilities || {})
    .filter(([_, value]) => value === true)
    .map(([key]) => key);

  return (
    <div className="min-h-screen bg-white" data-testid="property-detail-page">
      <SEOHead
        title={property.title}
        description={
          property.description
            ? String(property.description).slice(0, 160)
            : `${property.title} — view photos, price, and book directly with the owner on RoomHora.`
        }
        image={property.media?.[0]?.url}
        type="article"
        noIndex={usingDemoFallback}
        jsonLd={[
          listingJsonLd(property),
          breadcrumbJsonLd([
            { name: 'Home', url: `${window.location.origin}/` },
            { name: 'Search', url: `${window.location.origin}/search` },
            { name: property.title, url: window.location.href },
          ]),
        ]}
      />
      <Navbar />

      {usingDemoFallback && (
        <div className="pt-20 px-4">
          <div
            role="status"
            className="rh-container mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 py-3 text-sm text-amber-900"
            data-testid="backend-offline-banner"
          >
            <RefreshCw className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
            <div className="flex-1">
              <div className="font-semibold">You're viewing a sample listing</div>
              <div className="text-amber-800/90">
                We couldn't reach our servers, so this page shows demo content.
                Contact and booking actions are disabled until the connection is
                restored.
              </div>
            </div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="shrink-0 rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 transition"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Image Gallery */}
      <div className={usingDemoFallback ? 'pt-4' : 'pt-16'}>
        <div className="relative h-[60vh] md:h-[70vh]">
          <img
            src={images[currentImageIndex]}
            alt={property.title}
            className="w-full h-full object-cover cursor-pointer"
            onClick={() => setShowGallery(true)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={() => setCurrentImageIndex(i => i === 0 ? images.length - 1 : i - 1)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                data-testid="prev-image-btn"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => setCurrentImageIndex(i => i === images.length - 1 ? 0 : i + 1)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                data-testid="next-image-btn"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm">
            {currentImageIndex + 1} / {images.length}
          </div>

          {/* Action Buttons */}
          <div className="absolute top-4 right-4 flex gap-2">
            <button
              onClick={handleShare}
              className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
              data-testid="share-btn"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={handleFavorite}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                isFavorite ? 'bg-accent text-white' : 'bg-white/90 hover:bg-white'
              }`}
              data-testid="favorite-btn"
            >
              <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
            </button>
          </div>

          {/* Badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            {property.owner_verified && (
              <Badge className="bg-emerald-500 text-white border-0 text-sm">
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Verified Owner
              </Badge>
            )}
            <Badge variant="secondary" className="bg-white/90 text-slate-700 border-0 text-sm">
              {propertyTypeLabels[property.property_type]}
            </Badge>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="rh-container py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-10">
            {/* Title & Price */}
            <div>
              <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-4">
                {property.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-slate-600">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-accent" />
                  <span>
                    {[
                      property.location?.area,
                      property.location?.city,
                      property.location?.country
                    ].filter(Boolean).join(', ')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>Posted {new Date(property.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="mt-6">
                {/* Original Price (Source of Truth) */}
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-serif text-4xl font-bold text-slate-900">
                    {priceInfo.original}
                  </span>
                  {property.negotiable && (
                    <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                      Negotiable
                    </Badge>
                  )}
                </div>
                {/* Approximate conversion if different currency */}
                {priceInfo.showConversion && (
                  <p className="mt-1 text-slate-500">
                    {priceInfo.converted}
                  </p>
                )}
              </div>
              {property.deposit > 0 && (
                <p className="mt-2 text-slate-600">
                  Deposit: {formatPrice(property.deposit, property.currency)}
                </p>
              )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {property.details?.bedroom_count > 0 && (
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <Bed className="w-6 h-6 text-accent mb-2" />
                  <p className="font-bold text-slate-900">{property.details.bedroom_count}</p>
                  <p className="text-sm text-slate-500">Bedrooms</p>
                </div>
              )}
              {property.details?.bathroom_count > 0 && (
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <Bath className="w-6 h-6 text-accent mb-2" />
                  <p className="font-bold text-slate-900">{property.details.bathroom_count}</p>
                  <p className="text-sm text-slate-500">Bathrooms</p>
                </div>
              )}
              {property.details?.area_sqft > 0 && (
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <Square className="w-6 h-6 text-accent mb-2" />
                  <p className="font-bold text-slate-900">{property.details.area_sqft}</p>
                  <p className="text-sm text-slate-500">Sq. Ft.</p>
                </div>
              )}
              <div className="p-4 bg-slate-50 rounded-2xl">
                <Home className="w-6 h-6 text-accent mb-2" />
                <p className="font-bold text-slate-900">
                  {furnishingLabels[property.details?.furnishing] || 'Not specified'}
                </p>
                <p className="text-sm text-slate-500">Furnishing</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <h2 className="font-serif text-2xl text-slate-900 mb-4">About This Property</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                {property.description || 'No description provided.'}
              </p>
            </div>

            {/* Facilities */}
            {activeFacilities.length > 0 && (
              <div>
                <h2 className="font-serif text-2xl text-slate-900 mb-4">Amenities & Facilities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {activeFacilities.map((facility) => {
                    const Icon = facilityIcons[facility] || CheckCircle2;
                    const label = facility.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    return (
                      <div key={facility} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                        <Icon className="w-5 h-5 text-accent" />
                        <span className="text-slate-700">{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* House Rules */}
            <div>
              <h2 className="font-serif text-2xl text-slate-900 mb-4">House Rules</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'Smoking', allowed: property.rules?.smoking_allowed },
                  { label: 'Alcohol', allowed: property.rules?.alcohol_allowed },
                  { label: 'Pets', allowed: property.rules?.pets_allowed },
                  { label: 'Visitors', allowed: property.rules?.visitors_allowed },
                ].map((rule) => (
                  <div key={rule.label} className="flex items-center gap-3">
                    {rule.allowed ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <X className="w-5 h-5 text-red-500" />
                    )}
                    <span className="text-slate-700">{rule.label} {rule.allowed ? 'Allowed' : 'Not Allowed'}</span>
                  </div>
                ))}
              </div>
              {property.rules?.custom_rules && (
                <p className="mt-4 text-slate-600 p-4 bg-slate-50 rounded-xl">
                  {property.rules.custom_rules}
                </p>
              )}
            </div>

            {/* Tenant Preferences */}
            <div>
              <h2 className="font-serif text-2xl text-slate-900 mb-4">Ideal Tenants</h2>
              <div className="flex flex-wrap gap-2">
                {property.occupancy?.male_allowed && (
                  <Badge variant="outline" className="border-slate-200">Males</Badge>
                )}
                {property.occupancy?.female_allowed && (
                  <Badge variant="outline" className="border-slate-200">Females</Badge>
                )}
                {property.occupancy?.family_allowed && (
                  <Badge variant="outline" className="border-slate-200">Families</Badge>
                )}
                {property.occupancy?.couples_allowed && (
                  <Badge variant="outline" className="border-slate-200">Couples</Badge>
                )}
                {property.occupancy?.students_allowed && (
                  <Badge variant="outline" className="border-slate-200">Students</Badge>
                )}
                {property.occupancy?.professionals_allowed && (
                  <Badge variant="outline" className="border-slate-200">Professionals</Badge>
                )}
              </div>
              {property.occupancy?.max_renters && (
                <p className="mt-4 text-slate-600">
                  <Users className="w-4 h-4 inline mr-2" />
                  Maximum {property.occupancy.max_renters} renters
                </p>
              )}
            </div>

            {/* Location Map */}
            <div>
              <h2 className="font-serif text-2xl text-slate-900 mb-4">Location</h2>
              <div className="h-80 rounded-2xl overflow-hidden">
                {hasValidApiKey && isLoaded && property.location?.latitude && property.location?.longitude ? (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={{
                      lat: property.location.latitude,
                      lng: property.location.longitude,
                    }}
                    zoom={15}
                    options={{
                      disableDefaultUI: true,
                      zoomControl: true,
                    }}
                  >
                    <Marker
                      position={{
                        lat: property.location.latitude,
                        lng: property.location.longitude,
                      }}
                    />
                  </GoogleMap>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 relative flex items-center justify-center" style={{
                    backgroundImage: 'linear-gradient(rgba(148,163,184,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.15) 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                  }}>
                    {property.location?.latitude && property.location?.longitude && (
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <div className="w-6 h-6 bg-accent rounded-full border-2 border-white shadow-lg" />
                        <div className="w-3 h-3 bg-accent/30 rounded-full mx-auto -mt-1 animate-ping" />
                      </div>
                    )}
                    <div className="bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 shadow-sm">
                      <MapPin className="w-5 h-5 text-accent mx-auto mb-1" />
                      <p className="text-sm text-slate-600">Map available with API key</p>
                    </div>
                  </div>
                )}
              </div>
              <p className="mt-4 text-slate-600">
                <MapPin className="w-4 h-4 inline mr-2" />
                {[
                  property.location?.street_address,
                  property.location?.area,
                  property.location?.city,
                  property.location?.region,
                  property.location?.country,
                ].filter(Boolean).join(', ')}
              </p>
            </div>

            {/* Report Button */}
            <div className="pt-6 border-t border-slate-100">
              <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="text-slate-500 hover:text-red-500" data-testid="report-btn">
                    <Flag className="w-4 h-4 mr-2" />
                    Report This Listing
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-serif">Report Listing</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Reason</Label>
                      <Select value={reportReason} onValueChange={setReportReason}>
                        <SelectTrigger data-testid="report-reason-select">
                          <SelectValue placeholder="Select a reason" />
                        </SelectTrigger>
                        <SelectContent>
                          {reportReasons.map((reason) => (
                            <SelectItem key={reason} value={reason}>
                              {reason}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Additional Details (optional)</Label>
                      <Textarea
                        placeholder="Provide more details about the issue..."
                        value={reportDetails}
                        onChange={(e) => setReportDetails(e.target.value)}
                        data-testid="report-details-input"
                      />
                    </div>
                    <Button
                      onClick={handleReport}
                      disabled={reportLoading}
                      className="w-full bg-red-500 hover:bg-red-600"
                      data-testid="submit-report-btn"
                    >
                      {reportLoading ? 'Submitting...' : 'Submit Report'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Sidebar - Owner Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-soft">
                <div className="flex items-center gap-4 mb-6">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={property.owner_picture} />
                    <AvatarFallback className="bg-slate-200 text-slate-600 text-xl">
                      {property.owner_name?.[0] || 'O'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-serif text-xl font-bold text-slate-900">
                      {property.owner_name || 'Property Owner'}
                    </h3>
                    {property.owner_verified && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-0 mt-1">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                </div>

                {property.owner_phone && (
                  <a
                    href={`tel:${property.owner_phone}`}
                    className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl mb-3 hover:bg-slate-100 transition-colors"
                    data-testid="owner-phone"
                  >
                    <Phone className="w-5 h-5 text-accent" />
                    <span className="text-slate-700">{property.owner_phone}</span>
                  </a>
                )}

                {property.owner_email && (
                  <a
                    href={`mailto:${property.owner_email}`}
                    className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl mb-6 hover:bg-slate-100 transition-colors"
                    data-testid="owner-email"
                  >
                    <Mail className="w-5 h-5 text-accent" />
                    <span className="text-slate-700 truncate">{property.owner_email}</span>
                  </a>
                )}

                <Button
                  className="w-full bg-accent hover:bg-accent/90 rounded-full h-12 text-base font-medium"
                  onClick={() => {
                    if (property.owner_email) {
                      window.location.href = `mailto:${property.owner_email}?subject=Inquiry about ${property.title}`;
                    } else {
                      toast.info('Contact information not available');
                    }
                  }}
                  data-testid="contact-owner-btn"
                >
                  Contact Owner
                </Button>

                {isAuthenticated && user?.user_id !== property.owner_id && (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full h-12 text-base font-medium mt-3"
                    onClick={() => setBookingModalOpen(true)}
                    data-testid="request-booking-btn"
                  >
                    <CalendarCheck className="mr-2 h-4 w-4" />
                    Request Booking
                  </Button>
                )}

                {isAuthenticated && user?.user_id !== property.owner_id && (
                  <Button
                    variant="outline"
                    className="w-full rounded-full h-12 text-base font-medium mt-3"
                    onClick={() => navigate(`/messages?to=${property.owner_id}&property=${id}`)}
                    data-testid="message-owner-btn"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Send Message
                  </Button>
                )}

                {isOwner && user?.user_id === property.owner_id && (
                  <Button
                    variant="outline"
                    className="w-full rounded-full h-12 text-base font-medium mt-3"
                    onClick={() => navigate(`/property/${id}/edit`)}
                    data-testid="edit-property-btn"
                  >
                    Edit Property
                  </Button>
                )}
              </div>

              {/* Ad Placeholder */}
              <div className="ad-placeholder h-64 rounded-2xl">
                Ad Space - Sidebar
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      {property && (
        <div className="rh-container pb-12">
          <ReviewSection propertyId={id} ownerId={property.owner_id} />
        </div>
      )}

      {/* Booking Modal */}
      {property && (
        <BookingModal
          property={property}
          open={bookingModalOpen}
          onClose={() => setBookingModalOpen(false)}
        />
      )}

      {/* Gallery Modal */}
      <AnimatePresence>
        {showGallery && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          >
            <button
              onClick={() => setShowGallery(false)}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentImageIndex(i => i === 0 ? images.length - 1 : i - 1)}
              className="absolute left-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentImageIndex(i => i === images.length - 1 ? 0 : i + 1)}
              className="absolute right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <img
              src={images[currentImageIndex]}
              alt={property.title}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white">
              {currentImageIndex + 1} / {images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
